import { EmptyRows } from "@/components/app/lists/empty-rows";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/app/shell/page-header";
import { TextMessage } from "@/components/app/chat/text-message";
import { AiCard } from "@/components/app/patterns/ai-card";
import { AiSummary } from "@/components/app/patterns/ai-summary";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { LeadLink } from "@/components/app/lists/lead-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { LeadStatusPill } from "@/routes/_authenticated/_crm/crm/leads/-components/lead-status-pill";
import { guardRoute } from "@/lib/policy/route-guard";
import { conversationState } from "../-components/conversation-state";
import { useZoneFor } from "@/lib/facility/zone";
import { formatTimeInTimezone } from "@alm/domain";
import { ConversationFact } from "@/routes/_authenticated/_shelby/shelby/conversations/-components/conversation-fact";

export const Route = createFileRoute("/_authenticated/_shelby/shelby/conversations/$callId")({
  beforeLoad: ({ context }) => guardRoute(context.me, "shelby-conversations"),
  component: SalesConversation,
});

/** One conversation, whole: what was said, where the family came from, the lead it opened and every
 * step the assistant took — including the moment a person was alerted and the moment they replied,
 * which is what this screen is really for. */
function SalesConversation() {
  const zoneFor = useZoneFor();
  const { callId } = Route.useParams();
  const detail = useQuery({
    ...orpc.shelby.conversation.queryOptions({ input: { callId } }),
    refetchInterval: (query) => (query.state.data?.status === "active" ? 8_000 : false),
  });
  const conversation = detail.data;
  const state = conversation
    ? conversationState({
        status: conversation.status,
        mode: conversation.thread?.mode ?? null,
        heldByName: conversation.thread?.heldByName ?? null,
        handoffName: conversation.thread?.handoffName ?? "",
      })
    : null;

  return (
    <>
      <PageHeader
        title={conversation?.number ?? "Conversation"}
        meta={
          conversation
            ? `Started ${new Date(conversation.startedAt).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}${conversation.website ? " from the website" : ""}.`
            : undefined
        }
        actions={
          <>
            {conversation?.number ? (
              <Button asChild variant="outline" size="sm">
                <Link
                  to="/sarah/messages"
                  search={{ facility: conversation.facilityId, thread: conversation.number }}
                >
                  Open the thread
                </Link>
              </Button>
            ) : null}
            {conversation?.lead ? (
              <Button asChild variant="outline" size="sm">
                <Link to="/crm/leads/$leadId" params={{ leadId: conversation.lead.id }}>
                  Open lead
                </Link>
              </Button>
            ) : null}
            <Button asChild variant="ghost" size="sm">
              <Link to="/shelby/conversations">All conversations</Link>
            </Button>
          </>
        }
      />

      {detail.isError ? (
        <ReadFailed
          message="Could not load this conversation."
          error={detail.error}
          onRetry={() => void detail.refetch()}
          retrying={detail.isFetching}
        />
      ) : detail.isPending || !conversation ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
          <div className="flex flex-col gap-4">
            {/* Her write-up first, in the card every agent's write-up wears. */}
            {conversation.summary ? (
              <AiCard label="Written by the sales assistant">
                <CardContent>
                  <AiSummary
                    headline={conversation.summary.headline}
                    points={conversation.summary.keyPoints}
                    outcome={[{ label: "Next step", value: conversation.summary.followUp }]}
                  />
                </CardContent>
              </AiCard>
            ) : null}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2">
                <CardTitle>Conversation</CardTitle>
                {state ? <Badge variant={state.variant}>{state.label}</Badge> : null}
              </CardHeader>
              <CardContent className="space-y-4">
                {conversation.transcript.length === 0 ? (
                  <EmptyRows text="Nothing has been said yet." />
                ) : (
                  <ol className="flex flex-col gap-2">
                    {conversation.transcript.map((turn, index) => (
                      <li
                        // The transcript is an ordered log: two identical turns
                        // are two different things said, so position is identity.
                        // oxlint-disable-next-line no-array-index-key
                        key={`${index}-${turn.text.slice(0, 12)}`}
                      >
                        {/* Her turns wear the AI tint, as every text nobody typed does. */}
                        <TextMessage
                          align={turn.role === "assistant" ? "start" : "end"}
                          variant={turn.role === "assistant" ? "tinted" : "muted"}
                        >
                          <span className="sr-only">
                            {turn.role === "assistant" ? "Sales assistant: " : "Family: "}
                          </span>
                          {turn.text}
                        </TextMessage>
                      </li>
                    ))}
                  </ol>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Where it came from</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                {conversation.website ? (
                  <>
                    <ConversationFact label="Page">
                      {conversation.website.pageTitle || conversation.website.pageUrl || "-"}
                    </ConversationFact>
                    {conversation.website.interest ? (
                      <ConversationFact label="About">
                        {conversation.website.interest.replaceAll(/[-_]/g, " ")}
                      </ConversationFact>
                    ) : null}
                    {conversation.website.referrer ? (
                      <ConversationFact label="Referrer">
                        {conversation.website.referrer}
                      </ConversationFact>
                    ) : null}
                    {Object.keys(conversation.website.campaign ?? {}).length > 0 ? (
                      <ConversationFact label="Campaign">
                        {Object.entries(conversation.website.campaign)
                          .map(([key, value]) => `${key}=${value}`)
                          .join(", ")}
                      </ConversationFact>
                    ) : null}
                    <ConversationFact label="Pages viewed">
                      {conversation.website.pages}
                    </ConversationFact>
                    {conversation.website.device ? (
                      <ConversationFact label="Device">
                        {conversation.website.device}
                      </ConversationFact>
                    ) : null}
                  </>
                ) : (
                  <p className="text-muted-foreground">
                    {conversation.events.some((event) => event.event === "passed to sales")
                      ? "Texted the community's line; the front desk passed it to the sales assistant."
                      : "Texted the community's line."}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>The lead</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                {conversation.lead ? (
                  <>
                    <ConversationFact label="Name">
                      <LeadLink leadId={conversation.lead.id}>
                        {`${conversation.lead.firstName} ${conversation.lead.lastName ?? ""}`.trim()}
                      </LeadLink>
                    </ConversationFact>
                    <ConversationFact label="Stage">
                      <LeadStatusPill status={conversation.lead.status} />
                    </ConversationFact>
                    <ConversationFact label="Assigned to">
                      {conversation.lead.assignedToName ?? "Nobody yet"}
                    </ConversationFact>
                  </>
                ) : (
                  <EmptyRows text="No lead on file for this number." />
                )}
                {conversation.transfer ? (
                  <ConversationFact label="Sales person">
                    {conversation.transfer.name}
                    {conversation.transfer.section
                      ? ` (${conversation.transfer.section})`
                      : ""},{" "}
                    {conversation.transfer.status === "connected"
                      ? "replied"
                      : conversation.transfer.status === "missed"
                        ? "did not reply in time"
                        : "alerted"}
                  </ConversationFact>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>What happened</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="flex flex-col gap-1.5 text-xs">
                  {conversation.events.map((event, index) => (
                    // Same: the timeline is ordered, and two events can share
                    // an instant.
                    // oxlint-disable-next-line no-array-index-key
                    <li key={`${event.at}-${index}`} className="flex items-start gap-2">
                      <span className="text-muted-foreground w-16 shrink-0 tabular-nums">
                        {formatTimeInTimezone(event.at, zoneFor(conversation.facilityId))}
                      </span>
                      <span>
                        <Badge variant="outline">{event.event}</Badge>
                        {event.detail ? (
                          <span className="text-muted-foreground ml-1">{event.detail}</span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
